import { FaArrowDown, FaGithub, FaLinkedin, FaEnvelope, FaTwitter } from 'react-icons/fa'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import { heroData, statistics } from '../../data'
import VisibleBackground from '../animations/VisibleBackground'
import { FadeIn, SlideIn, ScaleIn } from '../animations/ScrollAnimations'

export default function Hero(): JSX.Element {
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  
  const rotateX = useTransform(mouseY, [-300, 300], [15, -15])
  const rotateY = useTransform(mouseX, [-300, 300], [-15, 15])

  const handleMouseMove = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    mouseX.set(event.clientX - centerX)
    mouseY.set(event.clientY - centerY)
  }

  const handleMouseLeave = () => {
    mouseX.set(0)
    mouseY.set(0)
  }

  return (
    <div 
      className="section bg-portfolio-black relative overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Visible Animated Background */}
      <VisibleBackground />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-portfolio-green/20 to-transparent"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-portfolio-green/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-portfolio-green/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <FadeIn direction="up" delay={0.2}>
              <div className="space-y-6">
                {/* Avatar Section */}
                <motion.div 
                  className="flex justify-center lg:justify-start"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.05 }}
                  >
                                         {/* Avatar Image */}
                     <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-green-400/30 shadow-2xl shadow-green-400/20">
                       <img 
                         src={heroData.avatar.src} 
                         alt={heroData.avatar.alt}
                         className="w-full h-full object-cover"
                         onError={(e) => {
                           // Fallback to placeholder if image doesn't exist
                           const target = e.target as HTMLImageElement
                           target.style.display = 'none'
                           target.nextElementSibling?.classList.remove('hidden')
                         }}
                       />
                       {/* Placeholder Avatar */}
                       <div className="w-full h-full bg-gradient-to-br from-green-400/20 to-green-600/40 flex items-center justify-center text-4xl font-bold text-white hidden">
                         {heroData.name.split(' ').map(n => n[0]).join('')}
                       </div>
                     </div>
                    
                    {/* Animated Ring */}
                    <motion.div 
                      className="absolute inset-0 rounded-full border-2 border-green-400/50"
                      animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [0.5, 0.8, 0.5]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    
                    {/* Status Indicator */}
                    <motion.div 
                      className="absolute bottom-2 right-2 w-6 h-6 bg-green-400 rounded-full border-4 border-portfolio-black"
                      animate={{ 
                        scale: [1, 1.2, 1],
                        boxShadow: ["0 0 0 0 rgba(149, 255, 49, 0.4)", "0 0 0 10px rgba(149, 255, 49, 0)", "0 0 0 0 rgba(149, 255, 49, 0.4)"]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </motion.div>
                </motion.div>

                <div className="space-y-4">
                  <motion.p 
                    className="text-green-400 font-semibold text-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {heroData.greeting}
                  </motion.p>
                  <motion.h1 
                    className="heading-1"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    style={{ rotateX, rotateY }}
                    whileHover={{ scale: 1.02 }}
                  >
                    {heroData.name} <span className="gradient-text">Adebayo</span>
                  </motion.h1>
                  <motion.h2 
                    className="text-2xl md:text-3xl font-semibold text-gray-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    whileHover={{ x: 10 }}
                  >
                    {heroData.title}
                  </motion.h2>
                  <motion.p 
                    className="text-large max-w-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    whileHover={{ scale: 1.01 }}
                  >
                    {heroData.description}
                  </motion.p>
                </div>
              </div>
            </FadeIn>

            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <motion.a 
                href={heroData.primaryButton.href} 
                className="btn-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {heroData.primaryButton.text}
              </motion.a>
              <motion.a 
                href={heroData.secondaryButton.href} 
                className="btn-secondary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {heroData.secondaryButton.text}
              </motion.a>
            </motion.div>

            {/* Social Links */}
            <motion.div 
              className="flex space-x-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.a
                href={heroData.socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-green-400 transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaGithub className="text-2xl" />
              </motion.a>
              <motion.a
                href={heroData.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-green-400 transition-colors"
                whileHover={{ scale: 1.2, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin className="text-2xl" />
              </motion.a>
              <motion.a
                href={heroData.socialLinks.x}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-green-400 transition-colors"
                whileHover={{ scale: 1.2, rotate: 10 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter className="text-2xl" />
              </motion.a>
              <motion.a
                href={`mailto:${heroData.socialLinks.email}`}
                className="text-gray-400 hover:text-green-400 transition-colors"
                whileHover={{ scale: 1.2, y: -2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaEnvelope className="text-2xl" />
              </motion.a>
            </motion.div>
          </div>

          {/* Right Content - Stats */}
          <div className="space-y-8">
            <SlideIn direction="right" delay={0.3}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: statistics.hero.years, label: "Years Experience" },
                  { value: statistics.hero.projects, label: "Projects Delivered" },
                  { value: statistics.hero.uptime, label: "Uptime Achieved" },
                  { value: statistics.hero.performance, label: "Deployment Time Reduced" }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    className="card text-center cursor-pointer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    whileHover={{ 
                      scale: 1.05, 
                      y: -5,
                      boxShadow: "0 20px 40px rgba(149, 255, 49, 0.2)"
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div 
                      className="text-3xl font-bold text-green-400 mb-2"
                      whileHover={{ scale: 1.1 }}
                    >
                      {stat.value}
                    </motion.div>
                    <div className="text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </SlideIn>

            <ScaleIn delay={0.6}>
              <motion.div 
                className="card cursor-pointer"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 20px 40px rgba(149, 255, 49, 0.15)"
                }}
              >
                <h3 className="text-xl font-semibold text-white mb-4">Core Expertise</h3>
                <div className="space-y-3">
                  {statistics.expertise.map((skill, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-center justify-between"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                      whileHover={{ x: 5 }}
                    >
                      <span className="text-gray-400">{skill.name}</span>
                      <div className="w-24 bg-gray-700 rounded-full h-2">
                        <motion.div 
                          className="bg-green-400 h-2 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${skill.percentage}%` }}
                          transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                          whileHover={{ scaleY: 1.5 }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </ScaleIn>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <motion.a 
            href="#about" 
            className="text-gray-400 hover:text-green-400 transition-colors cursor-pointer"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            whileHover={{ scale: 1.2 }}
          >
            <FaArrowDown className="text-2xl" />
          </motion.a>
        </motion.div>
      </div>
    </div>
  )
} 